import React from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { omitBy, isNil, debounce } from 'lodash';
import {withRouter } from 'withRouter'
import InfiniteScroll from 'react-infinite-scroller';
import { DEFAULT_PAGE_SIZE } from 'utils/constants';
import { getPaginationState, savePaginationState, getSortState, saveSortState, getCardViewState, saveCardViewState } from 'utils/tableStorage';
import { isRoleHigherThan } from 'utils/common';
import {
  getDetailRole,
} from 'containers/Settings/AccessRole/selectors';

import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import { FormattedMessage } from 'react-intl';
import { routeMapping } from 'routes';
import { Users } from 'react-feather';
import slugify from 'react-slugify';
import Pagination from 'components/Pagination';

import SearchField from 'components/SingleSearch';
import { EnumType } from 'json-to-graphql-query';
import ListSelectedItems from 'components/ListSelectedItems';
import Filter from 'components/Filter';
import { getFilterData } from 'components/Filter/filter';
import groupSaga from 'containers/Settings/UserGroup/saga';
import groupReducer from 'containers/Settings/UserGroup/reducer';
import messages from 'utils/messages';
import SecondaryModal from 'components/SecondaryModal';
import NotFound from 'containers/NotFoundPage';
import {
  getTrackerInfo,
} from 'containers/Settings/Workspace/selectors';
import {
  updateStartedTrackerRequest,
} from 'containers/Settings/Workspace/actions';
import { fetchUserRequest } from './actions';
import UserTable from './Table';
import filterColumns from './filter';
import Card from './Card';
import { CARD_VIEW, TABLE_VIEW } from './constants';
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectFetchUser,
} from './selectors';
import saga from './saga';
import roleSaga from '../AccessRole/saga';
import roleReducer from '../AccessRole/reducer';
import reducer from './reducer';
import { makeSelectFetchWorkspace } from 'containers/Settings/Workspace/selectors';

const USER_TABLE_ID = 'settings-user-table';

class UserList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      resetFilter: false,
      filteredColumns: [],
      showModal: true,
      currentPage: 1,
      limit: DEFAULT_PAGE_SIZE,
      filter: { single: [], range: [] },
      search: null,
      searchName: null,
      sort: { key: 'firstName', direction: 'asc' },
    };
  }

  componentDidMount() {
    if (!this.props.getUserList) {
      this.fetchUsers();
    }
  }

  fetchUsers = () => {
    const filter = this.state.filter || { single: [], range: [] };
    const single = this.state.searchName
      ? [...filter.single, this.state.search]
      : [...filter.single];

    const args = {
      limit: this.state.limit || DEFAULT_PAGE_SIZE,
      includeDemoData: this.props.workspace?.demoModeEnabled,
      offset:
        (this.state.currentPage || 1) <= 1
          ? 0
          : ((this.state.currentPage || 1) - 1) *
            (this.state.limit || DEFAULT_PAGE_SIZE),
      query: { range: filter.range, single },
      sort: {
        field: (this.state.sort && this.state.sort.key) || 'firstName',
        type:
          this.state.sort && this.state.sort.direction === 'asc'
            ? new EnumType('ASC')
            : new EnumType('DESC'),
      },
    };

    this.props.listRequest({ args: omitBy(args, isNil) });
  };

  fetchList = () => {
    if (this.props.getUserList) {
      this.props.getUserList();
    } else {
      this.fetchUsers();
    }
  };

  handleSetCurrentPage = (currentPage, limit) => {
    this.setState(
      {
        currentPage,
        limit: limit || this.state.limit || DEFAULT_PAGE_SIZE,
      },
      () => this.fetchList(),
    );
  };

  onSorting = (sort) => {
    saveSortState(USER_TABLE_ID, sort);
    this.setState(
      {
        sort,
        currentPage: 1,
        offset: 0,
        limit: this.state.limit,
      },
      () => {
        savePaginationState(USER_TABLE_ID, 1, this.state.limit);
        this.props.setCurrentPage && this.props.setCurrentPage(1);
        this.fetchList();
      },
    );
  };

  onCloseModal = () => {
    this.onSkip();
    this.setState({ showModal: false });
  };

  onRefreshCallback = () => {
    this.fetchList();
  };

  /* Filter End */
  isMore = () => {
    const { userList, loading } = this.props;
    if (loading) {
      return false;
    }

    return userList && userList.count > userList.data.length;
  };

  onFilter = (data) => {
    const { filterData, filteredColumns } = getFilterData(data);

    // When the parent owns the list state (getUserList prop is provided),
    // delegate to the parent's onFilterChange so its filter/search state is
    // updated before the list is refetched. Otherwise update local state.
    if (this.props.getUserList && this.props.onFilterChange) {
      this.setState({ resetFilter: false, filteredColumns }, () => {
        savePaginationState(USER_TABLE_ID, 1, this.state.limit);
        this.props.setCurrentPage && this.props.setCurrentPage(1);
        this.props.onFilterChange(filterData, filteredColumns);
      });
      return;
    }

    this.setState({ isLoading: true }, () => {
      this.setState(
        {
          resetFilter: false,
          isLoading: false,
          filteredColumns,
          filter: filterData,
          currentPage: 1,
          offset: 0,
          limit: this.state.limit,
        },
        () => {
          savePaginationState(USER_TABLE_ID, 1, this.state.limit);
          this.props.setCurrentPage && this.props.setCurrentPage(1);
          this.fetchList();
        },
      );
    });
  };

  onReset = () => {
    const query = new URLSearchParams(this.props.router.location.search);
    const role = query.get('role');
    const { workspace } = this.props;

    // When the parent owns the list state (getUserList prop is provided),
    // delegate the reset to the parent's onFilterChange / onSearchChange so
    // its filter/search state is cleared before the list is refetched.
    if (this.props.getUserList) {
      const emptyFilter = { single: [], range: [] };
      this.setState({ resetFilter: true, filteredColumns: [] }, () => {
        savePaginationState(USER_TABLE_ID, 1, this.state.limit);
        this.props.setCurrentPage && this.props.setCurrentPage(1);
        if (this.props.onFilterChange) {
          this.props.onFilterChange(emptyFilter, []);
        }
        if (this.props.onSearchChange) {
          this.props.onSearchChange(null, null);
        }
      });
      this.props.onReset && this.props.onReset();
      if (role) {
        return this.props.router.navigate(
          `/${workspace && slugify(workspace.name)}${
            routeMapping.SETTINGS_USER.path}`,
        );
      }
      return;
    }

    if (this.state.filter || this.state.searchName) {
      this.setState(
        {
          filter: {
            single: [],
            range: [],
          },
          search: '',
          searchName: '',
          resetFilter: true,
          currentPage: 1,
          offset: 0,
          limit: this.state.limit,
        },
        () => {
          savePaginationState(USER_TABLE_ID, 1, this.state.limit);
          this.props.setCurrentPage && this.props.setCurrentPage(1);
          this.fetchList();
        },
      );
    }
    if (this.props.onSearchChange) {
      this.props.onSearchChange(null, null);
    }
    
    this.props.onReset && this.props.onReset();

    if (role) {
      return this.props.router.navigate(
        `/${workspace && slugify(workspace.name)}${
          routeMapping.SETTINGS_USER.path}`,
      );
    }
  };

  onRowClick = (id) => {
    const { workspace } = this.props;

    return this.props.router.navigate(
      `/${workspace && slugify(workspace.name)}${
        routeMapping.SETTINGS_USER.path
      }/${id}`,
    );
  };

  onInvite = () => {
    this.onCloseModal();
    this.props.onCreate();
  };

  onSearch = (data) => {
    const payload = {
      name: 'username',
      operator: new EnumType('contains'),
      value: data,
    };

    // When the parent owns the list state (getUserList prop is provided),
    // delegate to the parent's onSearchChange so its search state is updated
    // before the list is refetched. Otherwise update local state.
    if (this.props.getUserList && this.props.onSearchChange) {
      this.setState({ resetFilter: false }, () => {
        savePaginationState(USER_TABLE_ID, 1, this.state.limit);
        this.props.setCurrentPage && this.props.setCurrentPage(1);
        this.props.onSearchChange(payload, data);
      });
      return;
    }

    this.setState(
      {
        resetFilter: false,
        search: payload,
        searchName: data,
        currentPage: 1,
        offset: 0,
        limit: this.state.limit,
      },
      () => {
        savePaginationState(USER_TABLE_ID, 1, this.state.limit);
        this.props.setCurrentPage && this.props.setCurrentPage(1);
        this.fetchList();
      },
    );
  };

  onSkip = () => {
    this.props.updateStartedTrackerRequest({
      task: 'inviteFirstTeamMember',
      skipped: true,
    });
  }

  // pagination
  onChangeLimit = (limit) => {
    if (this.props.setCurrentPage) {
      this.props.setCurrentPage(1, limit);
    }
  };

  setCurrentPage = (currentPage, limit) => {
    if (this.props.setCurrentPage) {
      this.props.setCurrentPage(currentPage, limit);
    }
  };

  render() {
    const userList = this.props.userList
      && this.props.userList.data
      && this.props.userList.data.map(user => ({
        userId: user.internalId || user.emailAddress,
        ...user,
      }));

    const {
      activeView,
      selectedIds,
      onSelect,
      preventRedirectOnClick,
      color,
      detailRole,
      disabledIds,
      isAssignedPage,
      hasCreatePermission,
    } = this.props;

    let disabledIdsArray = disabledIds;

    const query = new URLSearchParams(this.props.router.location.search);
    const role = query.get('role');
    const roleId = query.get('roleId');

    const { resetFilter } = this.state;

    const noAccessUser = this.props.userList && this.props.userList.data
      .filter(item => !isRoleHigherThan(detailRole.role, item.role))
      .map(item => item.id);

    if (isAssignedPage) {
      disabledIdsArray = disabledIds && disabledIds.concat(noAccessUser);

      disabledIdsArray = disabledIdsArray && this.props.userList && this.props.userList.data
        .filter(item => disabledIdsArray.includes(item.id))
        .map(item => item.id);
    }

    if (this.props.disabledNonVisoUser) {
      disabledIdsArray = disabledIds && disabledIds.concat(noAccessUser);

      disabledIdsArray = disabledIdsArray && this.props.userList && this.props.userList.data
        .filter(item => !item.email.endsWith('@viso.ai'))
        .map(item => item.id);
    }

    return (
      <>
        {this.props.userList
        && this.props.tracker
        && this.props.tracker.inviteFirstTeamMember === 'open'
          && !(this.props.searchName || this.state.searchName)
          && !((this.props.filter || this.state.filter).single.length)
          && !((this.props.filter || this.state.filter).range.length)
          && !this.props.userList.isLoading
          && this.props.userList.data.length === 1
          && this.state.showModal
          && hasCreatePermission && (
          <SecondaryModal onClose={this.onCloseModal}>
            <NotFound
              color={color}
              strokeWidth="1"
              parentClass="not-found__popup"
              logo={<Users size="42" />}
              title={<FormattedMessage {...messages.inviteNewTeamMembers} />}
              description={
                <FormattedMessage {...messages.inviteOthersToJoin} />
              }
              buttonText={<FormattedMessage {...messages.invitePeople} />}
              onAction={this.onInvite}
            />
          </SecondaryModal>
        )}
        <div className="popup__filters-bar filters-bar">
          {/* {!preventRedirectOnClick && ( */}
          <ListSelectedItems
            selectedNumber={selectedIds.length}
            search={this.props.searchName || this.state.searchName}
            onReset={this.onReset}
            filterData={this.props.filter || this.state.filter}
            containerClassName={color}
          />
          {/* )} */}
          <Filter
            defaultFilter={
              role
                ? [
                  {
                    field: 'status',
                    value: 'All',
                    isDefault: true,
                    columns: ['active', 'inactive', 'archived'],
                  },
                  {
                    field: 'role',
                    value: [role],
                    ids: [roleId],
                  },
                ]
                : [
                  {
                    field: 'status',
                    value: 'All',
                    isDefault: true,
                    columns: ['active', 'inactive', 'archived'],
                  },
                ]
            }
            customClass={color}
            resetFilter={resetFilter}
            onChange={this.onFilter}
            columns={filterColumns}
            data={this.props.userList && this.props.userList.data}
          >
            <FormattedMessage {...messages.search}>
              {msg => (
                <SearchField
                  onSearch={this.onSearch}
                  customClass={color}
                  resetSearch={this.state.resetFilter}
                  label={msg}
                />
              )}
            </FormattedMessage>
          </Filter>
        </div>


        {this.props.userList && this.props.userList.data && (
          <InfiniteScroll
            initialLoad={false}
            loader={(
              <div className="ml-3" key={0}>
                <FormattedMessage {...messages.loading} />
              </div>
            )}
            useWindow={false}
            getScrollParent={() => this.props.scrollRef}
          >
            {activeView !== CARD_VIEW  && (
              <>
                <UserTable
                  color={color}
                  multipleSelect
                  tableId={USER_TABLE_ID}
                  onDelete={this.props.onDelete}
                  isLoading={this.props.userList.isLoading}
                  filteredColumns={this.state.filteredColumns}
                  allowOnlySingleSelect={this.props.allowOnlySingleSelect}
                  onAssignRole={this.props.onAssignRole}
                  onUpdate={this.props.onUpdate}
                  disabledIds={disabledIdsArray}
                  onArchive={(id, e) => this.props.onArchive(id, e, this.onRefreshCallback)}
                  onDeactivate={(id, e) => this.props.onDeactivate(id, e, this.onRefreshCallback)}
                  onActivate={(id, e) => this.props.onActivate(id, e, this.onRefreshCallback)}
                  workspace={this.props.workspace}
                  loggedUserId={this.props.loggedUserId}
                  isAssignedPage={this.props.isAssignedPage}
                  preventRedirectOnClick={preventRedirectOnClick}
                  onRowClick={preventRedirectOnClick ? onSelect : this.onRowClick}
                  users={this.props.userList.data}
                  selectedItems={selectedIds}
                  hideSingleMenu={this.props.hideSingleMenu}
                  onSortChange={this.onSorting}
                  sort={this.props.sort || { key: 'firstName', direction: 'asc' }}
                  onSelect={onSelect}
                  isSelectedAll={this.selectAll}
                />
              </>
            )}
            {activeView === CARD_VIEW && (
              <Card
                color={color}
                data={userList}
                multipleSelect
                tableId={USER_TABLE_ID}
                onDelete={this.props.onDelete}
                isLoading={this.props.userList.isLoading}
                allowOnlySingleSelect={this.props.allowOnlySingleSelect}
                disabledIds={disabledIdsArray}
                workspace={this.props.workspace}
                loggedUserId={this.props.loggedUserId}
                hideSingleMenu={this.props.hideSingleMenu}
                onAssignRole={this.props.onAssignRole}
                onUpdate={this.props.onUpdate}
                isAssignedPage={this.props.isAssignedPage}
                onArchive={(id, e) => this.props.onArchive(id, e, this.onRefreshCallback)}
                onDeactivate={(id, e) => this.props.onDeactivate(id, e, this.onRefreshCallback)}
                onActivate={(id, e) => this.props.onActivate(id, e, this.onRefreshCallback)}
                selectedItems={selectedIds}
                onSelect={onSelect}
                preventRedirectOnClick={preventRedirectOnClick}
                onRowClick={preventRedirectOnClick ? onSelect : this.onRowClick}
                users={this.props.userList.data}
              />
            )}
          </InfiniteScroll>
        )}
        {!this.props.getUserList &&
          this.props.userList &&
          !!this.props.userList.count && (
            <Pagination
              tableId={USER_TABLE_ID}
              currentPage={this.state.currentPage || 1}
              setCurrentPage={this.handleSetCurrentPage}
              onChangeLimit={(limit) => this.handleSetCurrentPage(1, limit)}
              limit={this.state.limit || DEFAULT_PAGE_SIZE}
              totalPage={this.props.userList.count}
            />
        )}
      </>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  userList: makeSelectFetchUser(),
  errors: makeSelectError(),
  detailRole: getDetailRole(),
  tracker: getTrackerInfo(),
  workspace: makeSelectFetchWorkspace(),
});

const withConnect = connect(
  mapStateToProps,
  {
    listRequest: fetchUserRequest,
    updateStartedTrackerRequest,

  },
);

const withReducer = injectReducer({ key: 'user', reducer });
const withSaga = injectSaga({ key: 'user', saga });
const withGroupReducer = injectReducer({
  key: 'userGroup',
  reducer: groupReducer,
});
const withGroupSaga = injectSaga({
  key: 'userGroup',
  saga: groupSaga,
});
const withRoleReducer = injectReducer({
  key: 'role',
  reducer: roleReducer,
});
const withRoleSaga = injectSaga({
  key: 'role',
  saga: roleSaga,
});

export default compose(
  withReducer,
  withGroupReducer,
  withRoleReducer,
  withRoleSaga,
  withSaga,
  withRouter,
  withGroupSaga,
  withConnect,
)(UserList);
